import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, BellRing } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const waDigits = (p) => { let d = (p || "").replace(/\D/g, ""); if (d.length === 10) d = "52" + d; return d || null; };
const when = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })} a las ${d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}`;
};

export const waReminderText = (a, user) => {
  const link = `${window.location.origin}/pre-registro/${a.intake_token}`;
  const who = a.guardian_name ? `${a.guardian_name}` : a.patient_name;
  const where = [user?.clinic_name, user?.address].filter(Boolean).join(", ");
  return `Hola ${who}, le recordamos la cita de ${a.patient_name} con ${user?.name || "su médico"} el ${when(a.scheduled_at)}` +
    (where ? ` en ${where}` : "") + `.\n\nConfirme o cancele su asistencia aquí: ${link}` +
    (a.intake_completed ? "" : `\nY complete su pre-registro en el mismo enlace para agilizar su atención.`) +
    (user?.phone ? `\n\nDudas: ${user.phone}` : "") + `\n\nClinPed · Cerca de ti desde antes de tu consulta`;
};

export default function WhatsAppReminders() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const load = () => api.get("/appointments/wa-pending").then((r) => setItems(r.data || [])).catch(() => {});
    load();
    window.addEventListener("clinped:appointments-updated", load);
    return () => window.removeEventListener("clinped:appointments-updated", load);
  }, []);

  const send = (a) => {
    window.open(`https://wa.me/${waDigits(a.phone)}?text=${encodeURIComponent(waReminderText(a, user))}`, "_blank", "noopener");
    api.post(`/appointments/${a.appointment_id}/wa-sent`).catch(() => {});
    setItems((xs) => xs.filter((x) => x.appointment_id !== a.appointment_id));
  };

  if (!items.length) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="wa-reminders"
      className="bg-white rounded-3xl border border-stone-100 p-5 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-9 h-9 rounded-xl bg-[#25D366]/15 text-[#128C7E] flex items-center justify-center"><BellRing size={18} /></span>
        <div>
          <h3 className="font-outfit font-bold text-brand-ink">Recordatorios por WhatsApp pendientes</h3>
          <p className="text-xs text-muted-foreground">Citas de las próximas 24 h de pacientes sin correo. Un toque y el mensaje sale redactado.</p>
        </div>
      </div>
      <div className="space-y-2">
        <AnimatePresence>
          {items.map((a) => (
            <motion.div key={a.appointment_id} exit={{ opacity: 0, x: 20 }} data-testid={`wa-pending-${a.appointment_id}`}
              className="flex items-center justify-between gap-3 bg-stone-50 rounded-2xl px-3.5 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brand-ink truncate">{a.patient_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{when(a.scheduled_at)}</p>
              </div>
              <button data-testid={`wa-send-${a.appointment_id}`} onClick={() => send(a)}
                className="flex items-center gap-1.5 text-xs font-bold text-white bg-[#25D366] hover:bg-[#1EBE5A] px-3 py-2 rounded-xl transition-colors shrink-0">
                <MessageCircle size={15} /> Enviar</button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
